import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, Briefcase, Loader2, Search, User, X } from 'lucide-react';
import { fetchNavSearchResults } from '../utils/navSearch';

const isArchivedResult = (result) => {
    const phase = String(result?.phase || '').toLowerCase();
    const status = String(result?.status || '').toLowerCase();
    return phase === 'archived' || status === 'archived';
};

const getResultId = (result) => result?.rawValue || result?.value || result?.id;

const getResultMeta = (result) => {
    if (!result) return [];

    const meta = [];
    if (result.phase) meta.push({ key: 'phase', value: result.phase, archived: String(result.phase).toLowerCase() === 'archived' });
    if (result.status) meta.push({ key: 'status', value: result.status, archived: String(result.status).toLowerCase() === 'archived' });
    if (result.contactName) meta.push({ key: 'contact', value: result.contactName });
    if (result.createdDate) meta.push({ key: 'created', value: result.createdDate });
    if (result.caseType) meta.push({ key: 'case-type', value: result.caseType });
    if (result.assigned_user || result.assignedUser) meta.push({ key: 'assigned', value: result.assigned_user || result.assignedUser });

    return meta.filter(item => item.value);
};

const RagReferenceContextSelect = ({ value, onChange }) => {
    const [searchTerm, setSearchTerm] = useState(value?.label || '');
    const [isActive, setIsActive] = useState(false);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [includeArchived, setIncludeArchived] = useState(false);
    const searchRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (value?.label && !isActive) {
            setSearchTerm(value.label);
        }
    }, [value, isActive]);

    useEffect(() => {
        if (!isActive) return;

        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsActive(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isActive]);

    useEffect(() => {
        const query = searchTerm.trim();
        if (!isActive || query.length < 2) {
            setResults([]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const searchResults = await fetchNavSearchResults(query, { type: 'both', limit: 50 });
                if (!cancelled) setResults(searchResults.combined || []);
            } catch (error) {
                if (!cancelled) {
                    console.error('Error searching RAG reference contexts:', error);
                    setResults([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [isActive, searchTerm]);

    const visibleResults = useMemo(() => {
        if (includeArchived) return results;
        return results.filter(result => !isArchivedResult(result));
    }, [includeArchived, results]);

    const archivedCount = useMemo(() => results.filter(isArchivedResult).length, [results]);
    const hiddenArchivedCount = includeArchived ? 0 : archivedCount;

    const handleSelect = (result) => {
        const selected = {
            ...result,
            value: `${result.type}_${result.value}`,
            rawValue: result.value,
            label: result.label
        };
        onChange(selected);
        setSearchTerm(result.label || '');
        setResults([]);
        setIsActive(false);
    };

    const clearSelection = (event) => {
        event.stopPropagation();
        setSearchTerm('');
        setResults([]);
        onChange(null);
        inputRef.current?.focus();
    };

    return (
        <div ref={searchRef} className="library-rag-context-select">
            <div className="library-rag-context-input" onClick={() => inputRef.current?.focus()}>
                <Search size={16} />
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search cases & leads..."
                    value={searchTerm}
                    onChange={(event) => {
                        setSearchTerm(event.target.value);
                        if (!event.target.value.trim()) onChange(null);
                    }}
                    onFocus={() => setIsActive(true)}
                />
                {searchTerm && (
                    <button type="button" className="library-rag-context-clear" onClick={clearSelection} aria-label="Clear selected case or lead">
                        <X size={14} />
                    </button>
                )}
            </div>

            {isActive && (
                <div className="library-rag-context-panel">
                    <div className="library-rag-context-tools">
                        <span>Search scope</span>
                        <button
                            type="button"
                            className={`library-rag-archive-toggle${includeArchived ? ' active' : ''}`}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => setIncludeArchived(prev => !prev)}
                        >
                            <Archive size={13} />
                            Include archived
                        </button>
                    </div>

                    {loading && (
                        <div className="library-rag-context-state"><Loader2 size={14} className="spinner" /> Searching cases and leads…</div>
                    )}

                    {!loading && searchTerm.trim().length < 2 && (
                        <div className="library-rag-context-state">Start typing to search cases and leads.</div>
                    )}

                    {!loading && searchTerm.trim().length >= 2 && visibleResults.length === 0 && (
                        <div className="library-rag-context-state">
                            {hiddenArchivedCount > 0
                                ? `${hiddenArchivedCount} archived result${hiddenArchivedCount !== 1 ? 's are' : ' is'} hidden.`
                                : `No cases or leads found for “${searchTerm}”.`}
                        </div>
                    )}

                    {!loading && visibleResults.length > 0 && (
                        <>
                            <div className="library-rag-context-summary">
                                <span>{visibleResults.length} result{visibleResults.length !== 1 ? 's' : ''}</span>
                                {hiddenArchivedCount > 0 && <span>{hiddenArchivedCount} archived hidden</span>}
                            </div>
                            <div className="library-rag-context-results">
                                {visibleResults.map((result, index) => {
                                    const ResultIcon = result.type === 'case' ? Briefcase : User;
                                    const resultId = getResultId(result);
                                    const meta = getResultMeta(result);
                                    const archived = isArchivedResult(result);

                                    return (
                                        <button
                                            type="button"
                                            key={`${result.type}-${resultId || index}`}
                                            className={`library-rag-context-result${archived ? ' archived' : ''}`}
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => handleSelect(result)}
                                        >
                                            <span className="library-rag-context-result-icon"><ResultIcon size={16} /></span>
                                            <span className="library-rag-context-result-body">
                                                <span className="library-rag-context-result-title">
                                                    <span>{result.label}</span>
                                                    <small>{result.type} · {resultId}</small>
                                                </span>
                                                {meta.length > 0 && (
                                                    <span className="library-rag-context-result-meta">
                                                        {meta.map(item => (
                                                            <span key={item.key} className={item.archived ? 'archived' : undefined}>{item.value}</span>
                                                        ))}
                                                    </span>
                                                )}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default RagReferenceContextSelect;
